import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Chip, Collapse, InputAdornment, TextField, Typography, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EVENT_DICT } from './eventDict';
import MetricTooltip from './MetricTooltip';
import METRIC_DEFINITIONS from './metricDefinitions';
import { ANALYTICS_FONT_FAMILY } from './analyticsTypography';

/**
 * EventLegend
 *
 * Modernized event dictionary card. Active events (currently emitting to the
 * dashboard backend) appear first with a live count badge from the selected
 * date window. Inactive events appear below with a muted "reason" label
 * explaining exactly why they are not currently flowing into the dashboard.
 *
 * `categoryCounts` – optional; keyed by event name, value is the count for
 *   the active date range. Passed from AnalyticsTwo so the legend can show
 *   "[X] occurrences" inline for debugging/verification.
 */
export const EventLegend = ({ embedded = false, categoryCounts = {} }) => {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(false);

  const { active, toArchive, inactive } = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const all = q
      ? EVENT_DICT.filter(
          (item) =>
            item.alias.toLowerCase().includes(q) ||
            item.key.toLowerCase().includes(q) ||
            item.desc.toLowerCase().includes(q)
        )
      : EVENT_DICT;
    return {
      active: all.filter((e) => e.active && !e.toArchive),
      toArchive: all.filter((e) => e.toArchive),
      inactive: all.filter((e) => !e.active && !e.toArchive),
    };
  }, [filter]);

  const renderRow = (item, idx) => {
    const count = categoryCounts[item.key] ?? 0;

    return (
      <Box
        key={item.key}
        sx={{
          px: 2,
          py: 1.25,
          borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.06)',
          opacity: item.active ? 1 : 0.65,
        }}
      >
        {/* Header: alias + key, optional to-archive pill */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.3, gap: 1 }}
        >
          <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: item.active ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.5)',
                lineHeight: 1.3,
              }}
            >
              {item.alias}
            </Typography>
            <Typography
              sx={{
                fontFamily: ANALYTICS_FONT_FAMILY,
                fontSize: '0.63rem',
                color: 'rgba(0,0,0,0.28)',
                lineHeight: 1.3,
              }}
            >
              {item.key}
            </Typography>
          </Stack>

          {item.toArchive && (
            <Chip
              label="to archive"
              size="small"
              sx={{
                height: 16,
                fontSize: '0.58rem',
                fontWeight: 500,
                bgcolor: 'rgba(245,158,11,0.1)',
                color: 'rgba(160,100,0,0.85)',
                flexShrink: 0,
              }}
            />
          )}
        </Stack>

        {/* Description */}
        <Typography
          sx={{
            color: 'rgba(0,0,0,0.5)',
            fontSize: '0.75rem',
            lineHeight: 1.45,
          }}
        >
          {item.desc}
        </Typography>

        {/* Occurrence count */}
        <Typography
          sx={{
            display: 'block',
            mt: 0.4,
            fontSize: '0.65rem',
            color: 'rgba(0,0,0,0.28)',
            lineHeight: 1.4,
          }}
        >
          [{count.toLocaleString()}] occurrence{count !== 1 ? 's' : ''} in the selected timeframe.
        </Typography>

        {/* Reason — debug field, inactive only */}
        {!item.active && item.reason && (
          <Typography
            sx={{
              display: 'block',
              mt: 0.5,
              color: 'rgba(0,0,0,0.28)',
              fontSize: '0.55rem',
              fontFamily: ANALYTICS_FONT_FAMILY,
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {item.reason}
          </Typography>
        )}
      </Box>
    );
  };

  const renderGroup = (items, label, dividerColor = 'rgba(0,0,0,0.06)', dividerTextColor = 'rgba(0,0,0,0.3)') => {
    if (items.length === 0) return null;
    return (
      <Box>
        {label && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: dividerColor }} />
            <Typography
              sx={{
                color: dividerTextColor,
                fontSize: '0.63rem',
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: dividerColor }} />
          </Box>
        )}
        <Box
          sx={{
            borderRadius: '10px',
            border: '1px solid rgba(0,0,0,0.06)',
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          {items.map((item, i) => renderRow(item, i))}
        </Box>
      </Box>
    );
  };

  const content = (
    <Stack spacing={2}>
      <TextField
        size="small"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search events"
        variant="outlined"
        sx={{
          width: { xs: '100%', sm: 200 },
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontSize: '0.8rem',
            bgcolor: 'rgba(0,0,0,0.025)',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.1)' },
            '&.Mui-focused fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 14, color: 'rgba(0,0,0,0.45)' }} />
            </InputAdornment>
          ),
        }}
      />

      {renderGroup(active)}
      {renderGroup(toArchive, 'To Archive', 'rgba(245,158,11,0.2)', 'rgba(180,120,0,0.7)')}
      {renderGroup(inactive, 'Not currently firing')}

      {active.length === 0 && toArchive.length === 0 && inactive.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.45)' }}>
            No events match "{filter}".
          </Typography>
        </Box>
      )}
    </Stack>
  );

  if (embedded) return content;

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        sx={{
          width: '100%',
          borderRadius: '14px !important',
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 1.75 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          textAlign: 'left',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: 'rgba(0,0,0,0.88)',
              lineHeight: 1.2,
            }}
          >
            Glossary
          </Typography>
          <MetricTooltip
            {...METRIC_DEFINITIONS.eventGlossary}
            iconSize={13}
            iconColor="rgba(0,0,0,0.3)"
            srLabel="About the Event Glossary"
          />
        </Stack>
        <ExpandMoreIcon
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 220ms ease',
            color: 'rgba(0,0,0,0.5)',
            flexShrink: 0,
          }}
        />
      </ButtonBase>

      <Collapse in={expanded} timeout={250} unmountOnExit={false}>
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 } }}>
          {content}
        </Box>
      </Collapse>
    </Box>
  );
};

export default EventLegend;
